
import { withSSRAuth } from '@/lib/withSSRAuth';
import SettingsForm from "@/components/SettingsForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { signOut } from "next-auth/react";
import { toast } from "sonner";
import { Shield, Download, Eye } from "lucide-react";
import Link from "next/link";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const Settings = () => {
  const handleDeleteAccount = async () => {
    try {
      const password = prompt('Enter your password to confirm account deletion:');
      if (!password) return;

      const response = await fetch('/api/user/account', { 
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: password,
          confirmation: 'DELETE_MY_ACCOUNT'
        })
      });
      
      if (response.ok) {
        toast.success('Account deleted successfully');
        signOut({ callbackUrl: '/login' });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete account');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete account');
    }
  };


  return (
    <main className="flex-1 p-6 md:p-10 ml-0 md:ml-64">
      <div className="max-w-5xl mx-auto">
        <header className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your profile, documents, and account preferences.</p>
        </header>

        <div className="space-y-8">
          <SettingsForm />

          {/* Privacy & Data Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Privacy & Data
              </CardTitle>
              <CardDescription>
                Manage your personal data and understand how we collect and use your information.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link href="/settings/privacy">
                  <Card className="p-4 hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-full">
                        <Eye className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-medium">Data Audit</h3>
                        <p className="text-sm text-gray-600">View what data we collect</p>
                      </div>
                    </div>
                  </Card>
                </Link>
                
                <Link href="/settings/privacy">
                  <Card className="p-4 hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-full">
                        <Download className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-medium">Export Data</h3>
                        <p className="text-sm text-gray-600">Download your data</p>
                      </div>
                    </div>
                  </Card>
                </Link>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">Delete account</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                  <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteAccount}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button variant="outline" onClick={() => signOut({ callbackUrl: '/login' })}>Logout</Button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default withSSRAuth(Settings, { role: 'USER', type: 'DRIVER' });
